import { Component, computed, OnDestroy, OnInit, signal } from "@angular/core";
import { map, startWith, takeUntil } from "rxjs/operators";
import { combineLatest, firstValueFrom, Observable, Subject } from "rxjs";
import { Router, RouterLink } from "@angular/router";
import { Store } from "@ngrx/store";
import { NbDialogService } from "@nebular/theme";
import { AppState } from "../../../store/reducers";
import { AppContextViewModel, HelixVersionClient } from "@helix/client";
import { UpdateUserAppContext } from "../../../store/actions/app.actions";
import {
  getCurrentUser,
  getUserAppContext,
} from "../../../store/reducers/app.reducer";
import { VideoPlayerModalComponent } from "../video-player-model/video-player-modal.component";
import { ToggleSideBar } from "../../../store/actions/dashboard.actions";
import { AuthService } from "../../../auth/services/auth.service";
import { AsyncPipe, NgIf } from "@angular/common";
import { SvgSidebarCloseIconComponent } from "./svg-sidebar-close-icon/svg-sidebar-close-icon.component";
import { SvgSidebarOpenIconComponent } from "./svg-sidebar-open-icon/svg-sidebar-open-icon.component";
import { NavBreadcrumbComponent } from "./nav-breadcrumb/nav-breadcrumb.component";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { AvatarModule } from "primeng/avatar";
import { MenuModule } from "primeng/menu";
import { MenuItem } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { Tooltip } from "primeng/tooltip";
import { CdkCopyToClipboard } from "@angular/cdk/clipboard";
import { NbAccessChecker } from "@nebular/security";

@Component({
  selector: "ngx-header",
  styleUrls: ["./header.component.scss"],
  templateUrl: "./header.component.html",
  imports: [
    NgIf,
    SvgSidebarCloseIconComponent,
    SvgSidebarOpenIconComponent,
    NavBreadcrumbComponent,
    RouterLink,
    FontAwesomeModule,
    AsyncPipe,
    AvatarModule,
    MenuModule,
    ButtonModule,
    Tooltip,
    CdkCopyToClipboard,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  userEmail$: Observable<string>;

  componentDestroyed$: Subject<boolean> = new Subject();

  logoutItem = {
    label: "Log out",
    command: async () => {
      await this.router.navigate(["auth/logout"], {
        queryParamsHandling: "replace",
      });
    },
  };

  readonly userProfileMenuItems = signal<MenuItem[]>([this.logoutItem]);
  readonly apiVersion = signal<string>("");
  readonly webVersion = computed(() => {
    const versionTextInjectedDuringBuild = "1790328240";
    if (versionTextInjectedDuringBuild.startsWith("$")) {
      return "Local";
    }
    return versionTextInjectedDuringBuild;
  });
  readonly textForClipboard = computed(
    () => `HELIX Web v${this.webVersion()}\nHELIX API v${this.apiVersion()}`,
  );

  isSidebarVisible$: Observable<boolean> = this.store.select(
    (state) => state.dashboard.showSideMenu,
  );
  userAppContext$: Observable<AppContextViewModel>;
  logoUrl$: Observable<string>;
  trainingVideoTitle = "Training video";

  constructor(
    private authService: AuthService,
    private store: Store<AppState>,
    private router: Router,
    private nbDialogService: NbDialogService,
    private helixVersionClient: HelixVersionClient,
    private accessChecker: NbAccessChecker,
  ) {
    firstValueFrom(this.helixVersionClient.getApiVersion()).then(
      ({ version }) => {
        if (!version) {
          this.apiVersion.set("Unknown");
        } else if (version.startsWith("$")) {
          this.apiVersion.set("Local");
        } else {
          this.apiVersion.set("v" + version);
        }
      },
    );
  }

  private readonly YorkshireWaterAppContextId = 3;

  ngOnInit() {
    this.userAppContext$ = this.store.select(getUserAppContext);
    this.logoUrl$ = this.userAppContext$.pipe(
      map((ac) => {
        if (ac?.imageFileName) {
          return `assets/images/${ac.imageFileName}`;
        }
        return "assets/images/corp_helix_logo_100.png";
      }),
      startWith("assets/images/corp_helix_logo_100.png"),
    );

    this.userEmail$ = this.store
      .select(getCurrentUser)
      .pipe(map((user) => user?.email ?? "Not logged in"));

    combineLatest([
      this.accessChecker.isGranted("view", "app-context-page-view"),
      this.accessChecker.isGranted("view", "admin-view"),
      this.store.select((state: AppState) => state.app.appContexts),
      this.store.select(getUserAppContext),
    ])
      .pipe(
        map(
          ([
            showAppContextManager,
            showAdminTools,
            appContexts,
            userAppContext,
          ]) => {
            const menuItems: MenuItem[] = [];

            menuItems.push({
              label: "My Profile",
              routerLink: "/pages/user/profile",
              queryParamsHandling: "preserve",
              icon: "pi pi-user",
            });
            menuItems.push({ separator: true });

            menuItems.push({
              label: "Get Support",
              icon: "pi pi-flag",
              command: () => {
                window.open(
                  "https://binniesdigital.freshdesk.com/support/home",
                  "_blank",
                );
              },
            });
            menuItems.push({ separator: true });

            if (
              userAppContext?.appContextId === this.YorkshireWaterAppContextId
            ) {
              menuItems.push({
                label: this.trainingVideoTitle,
                icon: "pi pi-graduation-cap",
                command: () => {
                  this.nbDialogService.open(VideoPlayerModalComponent, {
                    context: {
                      videoSrc:
                        "https://stonsiteprod001.blob.core.windows.net/ecoxtraining/Helix/AC3/YorkshireWater-HELIX-UserGuidance.mp4",
                    },
                  });
                },
              });
              menuItems.push({ separator: true });
            }

            if (showAppContextManager) {
              menuItems.push({
                label: "App Context Manager",
                icon: "pi pi-list-check",
                routerLink: "/pages/manager/context-manager",
              });
              menuItems.push({ separator: true });
            }
            if (showAdminTools) {
              menuItems.push({
                label: "Admin Tools",
                icon: "pi pi-cog",
                routerLink: "/pages/admin/home",
              });
              menuItems.push({ separator: true });
            }

            appContexts
              .filter((x) => !x.isCurrentAppContext)
              .forEach((ac: AppContextViewModel) => {
                const menuItem: MenuItem = {
                  label: ac.name,
                  state: {
                    id: ac.appContextId,
                    isAppContext: true,
                    name: ac.name,
                    description: ac.description,
                    imageFileName: ac.imageFileName,
                  },
                  command: () => {
                    this.store.dispatch(
                      new UpdateUserAppContext({ id: ac.appContextId }),
                    );
                  },
                };

                menuItems.push(menuItem);
                menuItems.push({ separator: true });
              });

            menuItems.push(this.logoutItem);
            menuItems.push({ separator: true });

            return menuItems;
          },
        ),
        takeUntil(this.componentDestroyed$),
      )
      .subscribe((menuItems) => this.userProfileMenuItems.set(menuItems));
  }

  toggleSidebar() {
    this.store.dispatch(new ToggleSideBar());
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
